// src/components/admin/TypeChangeDetailsModal.tsx

import React from 'react';
import { X, Building2, User, FileText, Calendar, Mail, Clock } from 'lucide-react';
import TypeChangeStatusBadge from './TypeChangeStatusBadge';

interface TypeChangeDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    request: {
        id: number;
        vendor: {
            id: number;
            business_name: string;
            business_email: string;
            user?: {
                first_name: string;
                last_name: string;
                email: string;
            };
        };
        current_type: string;
        requested_type: string;
        reason: string;
        documents: { name: string; path: string; size: number }[];
        status: 'pending' | 'approved' | 'rejected';
        requested_at: string;
        reviewed_at?: string;
        reviewed_by?: string;
    };
}

const TypeChangeDetailsModal: React.FC<TypeChangeDetailsModalProps> = ({
    isOpen,
    onClose,
    request,
}) => {
    if (!isOpen) return null;

    const formatType = (type: string) => {
        const types: Record<string, string> = {
            individual: 'Individual',
            sole_proprietor: 'Sole Proprietor',
            llc: 'LLC',
            corporation: 'Corporation',
            partnership: 'Partnership',
            non_profit: 'Non-Profit',
        };
        return types[type] || type;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <FileText className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Request Details</h3>
                            <p className="text-sm text-gray-500">{request.vendor.business_name}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    {/* Status */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">Status</span>
                        <TypeChangeStatusBadge status={request.status} />
                    </div>

                    {/* Vendor Info */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-700">{request.vendor.business_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-700">{request.vendor.business_email}</span>
                        </div>
                    </div>

                    {/* Type Change */}
                    <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Current Type</span>
                            <span className="font-medium">{formatType(request.current_type)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Requested Type</span>
                            <span className="font-medium text-blue-600">{formatType(request.requested_type)}</span>
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-700">{request.reason}</p>
                        </div>
                    </div>

                    {/* Documents */}
                    {request.documents && request.documents.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Documents ({request.documents.length})
                            </label>
                            <div className="space-y-1">
                                {request.documents.map((doc, index) => (
                                    <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                        <FileText className="h-4 w-4 text-gray-400" />
                                        <span>{doc.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Timestamps */}
                    <div className="pt-2 border-t border-gray-200 space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="h-4 w-4" />
                            <span>Requested: {formatDate(request.requested_at)}</span>
                        </div>
                        {request.reviewed_at && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Clock className="h-4 w-4" />
                                <span>Reviewed: {formatDate(request.reviewed_at)}</span>
                                {request.reviewed_by && (
                                    <span className="text-gray-400">by {request.reviewed_by}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TypeChangeDetailsModal;